import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCopyToClipboard } from '../components/CopyButton';
import { ErrorDisplay } from '../components/ErrorDisplay';
import { Layout } from '../components/Layout';
import { Button, Input } from '../components/ui';
import { Spinner } from '../components/ui/Spinner';
import { CheckIcon, LockIcon } from '../components/Icons';
import { downloadFile, triggerDownload } from '../services/swarm/download';
import { useAuth } from '../contexts/AuthContext';
import { getAccount, verifyPassword } from '../services/storage';
import { hexToBytes } from '../services/swarm/encryption';
import { logger } from '../utils/logger';
import { BrowserCryptoProvider } from '@fairdrop/core/crypto/browser';
import {
  decryptSwarmRef,
  deserializeEncryptedSwarmRef,
} from '@fairdrop/core/crypto';

// Singleton crypto provider
const cryptoProvider = new BrowserCryptoProvider();

type DownloadState = 'loading' | 'ready' | 'complete' | 'error' | 'need_login';

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

export function Download() {
  const { reference, encryptedRef, filename } = useParams<{
    reference?: string;
    encryptedRef?: string;
    filename?: string;
  }>();
  const navigate = useNavigate();
  const { username: activeSubdomain } = useAuth();

  const { copy, isCopied } = useCopyToClipboard();
  const [state, setState] = useState<DownloadState>('loading');
  const [error, setError] = useState<string | null>(null);
  const [fileInfo, setFileInfo] = useState<{
    data: Uint8Array;
    contentType: string;
    filename: string;
  } | null>(null);

  // Login state for encrypted refs
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [decryptedRef, setDecryptedRef] = useState<string | null>(null);

  // Determine if this is an encrypted ref download
  const isEncryptedRef = !!encryptedRef;

  // Get the effective reference to use
  const effectiveRef = decryptedRef || reference;

  // Handle encrypted ref decryption
  const handleDecryptRef = async () => {
    if (!encryptedRef || !activeSubdomain) return;

    setLoginError('');

    const account = getAccount(activeSubdomain);
    if (!account) {
      setLoginError('Account not found');
      return;
    }

    // Verify password
    const isValid = await verifyPassword(password, account.passwordHash);
    if (!isValid) {
      setLoginError('Incorrect password');
      return;
    }

    try {
      // Deserialize and decrypt the Swarm reference
      const encrypted = deserializeEncryptedSwarmRef(encryptedRef);
      const privateKeyBytes = hexToBytes(account.privateKey);
      const swarmRef = await decryptSwarmRef(cryptoProvider, encrypted, privateKeyBytes);

      setDecryptedRef(swarmRef);
      // Will trigger the file load effect
    } catch (err) {
      logger.error('Failed to decrypt reference:', err);
      setLoginError('Failed to decrypt. Is this file for your account?');
    }
  };

  // Check if login is needed for encrypted ref
  useEffect(() => {
    if (isEncryptedRef && !decryptedRef) {
      if (!activeSubdomain) {
        setState('need_login');
      } else {
        // User is logged in but needs password to decrypt
        setState('need_login');
      }
    }
  }, [isEncryptedRef, decryptedRef, activeSubdomain]);

  // Load file info on mount or when ref is decrypted
  useEffect(() => {
    // Skip if encrypted ref and not yet decrypted
    if (isEncryptedRef && !decryptedRef) return;

    if (!effectiveRef) {
      setError('No file reference provided');
      setState('error');
      return;
    }

    const loadFile = async () => {
      try {
        setState('loading');
        const result = await downloadFile(effectiveRef);
        setFileInfo({
          data: result.data,
          contentType: result.contentType,
          filename: filename || result.filename,
        });
        setState('ready');
      } catch (err) {
        logger.error('Download failed:', err);
        setError(err instanceof Error ? err.message : 'Failed to load file');
        setState('error');
      }
    };

    loadFile();
  }, [effectiveRef, filename, isEncryptedRef, decryptedRef]);

  const handleDownload = () => {
    if (!fileInfo) return;

    triggerDownload(fileInfo.data, fileInfo.filename, fileInfo.contentType);

    // Track successful download for H001 validation
    if (typeof window !== 'undefined' && (window as any).posthog) {
      (window as any).posthog.capture('file_downloaded', {
        file_size: fileInfo.data.byteLength,
        encrypted: isEncryptedRef,
      });
    }

    setState('complete');
  };

  const renderContent = () => {
    switch (state) {
      case 'loading':
        return (
          <div className="content-centered">
            <Spinner size="lg" />
            <p style={{ marginTop: 24 }}>Loading file...</p>
            {filename && <p style={{ marginTop: 8, opacity: 0.7 }}>{filename}</p>}
          </div>
        );

      case 'error':
        return (
          <div className="content-centered">
            {filename && <p style={{ marginBottom: 16, opacity: 0.7 }}>{filename}</p>}
            <ErrorDisplay
              error={error || 'An unexpected error occurred.'}
              context={{ reference: effectiveRef || undefined }}
              onRetry={() => window.location.reload()}
              onHome={() => { window.location.href = '/'; }}
            />
          </div>
        );

      case 'need_login':
        return (
          <div className="content-centered">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginBottom: 16 }}>
              <LockIcon />
              <h1 className="heading-lg" style={{ margin: 0 }}>Encrypted File</h1>
            </div>
            <p style={{ marginBottom: 32, opacity: 0.8 }}>
              This file was sent to you with privacy protection.<br />
              Enter your password to unlock it.
            </p>

            {!activeSubdomain ? (
              <div style={{ textAlign: 'center' }}>
                <p style={{ marginBottom: 16 }}>You need to log in to access this file.</p>
                <Button variant="outline" onClick={() => navigate('/account')}>
                  Go to Login
                </Button>
              </div>
            ) : (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleDecryptRef();
                }}
                style={{ width: '100%', maxWidth: 400, marginLeft: 'auto', marginRight: 'auto' }}
              >
                <p style={{ marginBottom: 16, fontSize: 14 }}>
                  Logged in as <strong>{activeSubdomain}</strong>
                </p>
                <Input
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoFocus
                />

                {loginError && (
                  <p style={{ color: '#e74c3c', fontSize: 14, textAlign: 'center', marginTop: 8 }}>
                    {loginError}
                  </p>
                )}

                <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginTop: 24 }}>
                  <Button type="submit" variant="outline">
                    Unlock File
                  </Button>
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => navigate('/')}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>
        );

      case 'ready':
        return (
          <div className="content-centered">
            <h1 className="heading-lg">Download File</h1>

            <div style={{
              width: '100%',
              maxWidth: 500,
              marginTop: 32,
              marginLeft: 'auto',
              marginRight: 'auto'
            }}>
              <div className="detail-row">
                <span className="detail-row-label">File name</span>
                <span>{fileInfo?.filename}</span>
              </div>
              <div className="detail-row">
                <span className="detail-row-label">Size</span>
                <span>{fileInfo ? formatFileSize(fileInfo.data.length) : ''}</span>
              </div>
              <div className="detail-row">
                <span className="detail-row-label">Type</span>
                <span>{fileInfo?.contentType}</span>
              </div>
            </div>

            <Button
              variant="outline"
              style={{ marginTop: 32 }}
              onClick={handleDownload}
            >
              Download
            </Button>
          </div>
        );

      case 'complete':
        return (
          <div className="content-centered">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
              <CheckIcon style={{ width: 36, height: 36, display: 'block', flexShrink: 0 }} />
              <h2 style={{ fontSize: 32, lineHeight: 1, margin: 0 }}>Downloaded!</h2>
            </div>

            <p style={{ marginTop: 24 }}>
              {fileInfo?.filename}
            </p>

            <div style={{ display: 'flex', gap: 16, marginTop: 32 }}>
              <Button variant="outline" onClick={handleDownload}>
                Download Again
              </Button>
              <Button
                variant="outline"
                onClick={() => copy(window.location.href, 'link')}
              >
                {isCopied('link') ? 'Copied!' : 'Copy Link'}
              </Button>
            </div>

            <button
              className="link-button"
              style={{ marginTop: 32 }}
              onClick={() => { window.location.href = '/'; }}
            >
              Go Home
            </button>
          </div>
        );
    }
  };

  return (
    <Layout theme={state === 'complete' ? 'dark' : 'green'}>
      {renderContent()}
    </Layout>
  );
}
